// src/pages/Blog.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../services/firebase";
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './Blog.css';
import { useTranslation } from 'react-i18next';
import { pickDocField, pickCategoryLabel } from '../utils/i18nContent';

const localeMap = { fr: 'fr-FR', en: 'en-US', nl: 'nl-BE' };

function toDate(value) {
  if (!value) return null;
  if (value.seconds) return new Date(value.seconds * 1000);
  if (typeof value === 'string') return new Date(value);
  if (value instanceof Date) return value;
  return null;
}

export default function Blog() {
  const { t, i18n } = useTranslation('blog');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        // PAS d'orderBy → pas d'index composite requis
        const q = query(collection(db, "blog_posts"), where("status", "==", "published"));
        const snap = await getDocs(q);
        const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // tri côté client sur createdAt desc
        items.sort((a, b) => (toDate(b.createdAt)?.getTime() ?? 0) - (toDate(a.createdAt)?.getTime() ?? 0));
        setArticles(items);
      } catch (err) {
        console.error("Erreur lors de la récupération des articles:", err);
        setArticles([]); // évite l'état indéterminé
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  return (
    <div className="blog-container">
      <SEO title={t('seo.title')} description={t('seo.description')} keywords={t('seo.keywords')} />
      <div className="container">
        <h1>{t('title')}</h1>
        <p className="blog-subtitle">{t('subtitle')}</p>

        {loading ? (
          <div className="loading">{t('list.loading')}</div>
        ) : articles.length ? (
          <div className="articles-grid">
            {articles.map(article => {
              const title = pickDocField(article, 'title', i18n.language, 'fr');
              const excerpt = pickDocField(article, 'excerpt', i18n.language, 'fr') || t('list.excerptFallback');
              const cover = article.coverImage || '';
              const category = pickCategoryLabel(article.category, i18n.language, 'fr');
              const d = toDate(article.createdAt);
              const dateStr = d ? d.toLocaleDateString(localeMap[i18n.language] || 'fr-FR') : '';
              return (
                <article className="article-card" key={article.id}>
                  <div className="article-image">
                    <div
                      className={`image-placeholder${cover ? ' has-cover' : ''}`}
                      style={cover ? { backgroundImage: `url(${cover})` } : {}}
                      aria-label={title}
                    >
                      {category && <span className="category-badge">{category}</span>}
                    </div>
                  </div>
                  <div className="article-content">
                    <h2>{title}</h2>
                    <p className="article-excerpt">{excerpt}</p>
                    <div className="article-meta">
                      {dateStr && <span className="article-date">{dateStr}</span>}
                    </div>
                    <Link to={`/blog/${article.slug || article.id}`} className="read-more">
                      {t('list.readMore')} →
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <p className="no-articles">{t('list.empty')}</p>
        )}
      </div>
    </div>
  );
}